import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '@mui/material';

/**
 * MuiButton - A wrapper around Material UI Button that accepts the same props as Bloomer Button
 * 
 * This component maps Bloomer Button props to Material UI Button props for easy migration
 */
const MuiButton = ({
  className,
  isColor,
  isSize,
  isOutlined,
  isInverted,
  isStatic,
  isRounded,
  isLoading,
  isActive,
  isFocused,
  isHovered,
  isText,
  isDisabled,
  isFullWidth,
  children,
  onClick,
  style,
  ...props
}) => {
  // Map Bloomer color props to MUI color props
  const getColor = () => {
    if (!isColor) {
      return 'primary';
    }
    
    switch (isColor.toLowerCase()) {
      case 'primary': return 'primary';
      case 'info': return 'info';
      case 'success': return 'success';
      case 'warning': return 'warning';
      case 'danger': return 'error';
      case 'white': return 'default';
      case 'light': return 'default';
      case 'dark': return 'default';
      case 'black': return 'default';
      default: return 'primary';
    }
  };

  // Map Bloomer size props to MUI size props
  const getSize = () => {
    if (!isSize) {
      return 'medium';
    }
    
    switch (isSize.toLowerCase()) {
      case 'small': return 'small';
      case 'medium': return 'medium';
      case 'large': return 'large';
      default: return 'medium';
    }
  };

  // Combine custom styles and classes
  const getStyles = () => {
    const customStyles = { ...style };
    
    if (className && className.includes('transparent-elipses')) {
      customStyles.minWidth = 'unset';
      customStyles.padding = 0;
      customStyles.backgroundColor = 'transparent';
    }
    
    return customStyles;
  };

  // Determine the button variant
  const getVariant = () => {
    if (isOutlined) {
      return 'outlined';
    }
    if (isText) {
      return 'text';
    }
    return 'contained';
  };

  return (
    <Button
      variant={getVariant()}
      color={getColor()}
      size={getSize()}
      disabled={isDisabled || isStatic}
      fullWidth={isFullWidth}
      onClick={onClick}
      className={className}
      style={getStyles()}
      {...props}
    >
      {isLoading ? 'Loading...' : children}
    </Button>
  );
};

MuiButton.propTypes = {
  className: PropTypes.string,
  isColor: PropTypes.string,
  isSize: PropTypes.string,
  isOutlined: PropTypes.bool,
  isInverted: PropTypes.bool,
  isStatic: PropTypes.bool,
  isRounded: PropTypes.bool,
  isLoading: PropTypes.bool,
  isActive: PropTypes.bool,
  isFocused: PropTypes.bool,
  isHovered: PropTypes.bool,
  isText: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  onClick: PropTypes.func,
  style: PropTypes.object,
  children: PropTypes.node,
};

MuiButton.defaultProps = {
  className: '',
  isColor: 'primary',
  isSize: 'medium',
  isOutlined: false,
  isInverted: false,
  isStatic: false,
  isRounded: false,
  isLoading: false,
  isActive: false,
  isFocused: false,
  isHovered: false,
  isText: false,
  isDisabled: false,
  isFullWidth: false,
  style: {},
  onClick: () => {},
  children: null,
};

export default MuiButton;
